<template>
  <div class="app-header container-fluid">
    <div class="row">
      <div class="col-lg-6 left-side">
        <router-link to="/">
          <img src="~@/assets/images/desktop/logo.svg" class="logo" />
        </router-link>
      </div>
      <div class="col-lg-6 right-side text-right" v-if="user && user.uuid">
        <span class="header-language">
          <label class="header-language-label">{{ $t("language") }}</label>
          <select v-model="selectedLanguage" class="language-select" @change="onLanguageChange">
            <option v-for="language in languages" :key="language.value" :value="language.value">
              {{ language.label }}
            </option>
          </select>
        </span>
        <span class="company-name-holder">
          <router-link to="/" class="company-name secondary-title" v-if="domain && domain.company">
            {{ domain.company }}
          </router-link>
        </span>
        <span class="company-filters">
          <router-link to="/my-company/departments">
            <img src="~@/assets/images/desktop/icons/filters.svg" />
          </router-link>
        </span>
        <span class="company-filter-separator">
          <img src="~@/assets/images/desktop/icons/vertical-separator.svg" />
        </span>
        <span class="user-profile">
          <img :src="user && user.photo_url" class="rounded-circle profile-photo" />
          <span class="fs-small username c-pointer" @click.stop="toggleAccountDDL">
            {{ user && user.name }}
          </span>
          <img src="~@/assets/images/desktop/icons/arrow-down.svg" class="arrow-icon" @click.stop="toggleAccountDDL" />

          <!-- dropdown -->
          <div id="dropdown-menu" class="dropdown-menu" :class="[showAccountDDL ? 'd-block' : 'd-none']" @click.stop>
            <router-link to="/settings/profile" class="dropdown-item pl-3 fs-small gray80">
              <img src="~@/assets/images/desktop/icons/controls/cog.svg" class="icon-svg" />
              {{ $t("common.settings") }}
            </router-link>
            <div class="dropdown-divider"></div>
            <a v-on:click.prevent="logout" class="dropdown-item pl-3 fs-small gray80 c-pointer">
              <img src="~@/assets/images/desktop/icons/controls/logout.svg" class="icon-svg" />
              {{ $t("logout") }}
            </a>
            <div class="dropdown-divider"></div>
            <div class="text-right fs-small version">v{{ version }}</div>
          </div>
        </span>
      </div>
      <div class="col-lg-6 right-side text-right login" v-else>
        <router-link to="/auth/login" class="company-name secondary-title">
          <span class="login_text">{{ $t("login") }}</span>
        </router-link>
      </div>
    </div>

    <div v-if="showAccountDDL" class="app-overlay" @click="closeAccountDDL"></div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getTitle, isDevelopment, isLocal, isProduction, isStaging } from "~/helpers/envHelper";
import packageJson from "/package.json";

export default {
  name: "AppHeader",
  components: {},
  middleware: "auth",

  data() {
    return {
      userData: this.user ? this.user : {},
      showAccountDDL: false,
      selectedLanguage: "en",
      languages: [
        { value: "en", label: "EN" },
        { value: "hu", label: "HU" },
        { value: "ro", label: "RO" },
      ],
    };
  },

  computed: {
    ...mapGetters({
      user: "auth/user",
      domain: "auth/domain",
      currentDepartment: "auth/currentDepartment",
    }),
    version() {
      return packageJson.version;
    },
    appName() {
      return this.$t("appname");
    },
    title() {
      return getTitle();
    },
    isDevelopment() {
      return isDevelopment();
    },
    isLocal() {
      return isLocal();
    },
    isStaging() {
      return isStaging();
    },
    isProduction() {
      return isProduction();
    },
    role() {
      if (this.user && this.user.current_role) {
        return this.user.current_role.name;
      }
    },
    rolePermissions() {
      if (this.role && this.user.current_role.permissions) {
        return (
          this.$t("your_permissions") +
          ": \n " +
          this.user.current_role.permissions
            .map((item) => {
              return item.name + `(${this.getAccessText(item)})`;
            })
            .join(" + ")
        );
      }
    },
    department() {
      if (this.currentDepartment) {
        if (this.currentDepartment != null) {
          return this.currentDepartment.department_name;
        }
      }

      return "Select department";
    },
  },
  methods: {
    async logout({ app }) {
      // Log out the user.
      await this.$store.dispatch("auth/logout");

      // Redirect to login.
      this.$router.push({ path: "/auth/login" });
    },
    getAccessText(permission) {
      let p = getModuleAccess(this.user, permission.code);
      if (p && p.access) {
        return Object.keys(p.access)
          .filter((k) => {
            return Boolean(p.access[k]);
          })
          .join();
      }
    },
    // ...existing methods...
    toggleAccountDDL() {
      this.showAccountDDL = !this.showAccountDDL;
    },
    closeAccountDDL() {
      this.showAccountDDL = false;
    },
    _onEsc(e) {
      if (e.key === "Escape") this.closeAccountDDL();
    },
    async onLanguageChange() {
      const nextLanguage = (this.selectedLanguage || "en").toLowerCase();
      const currentLanguage = (this.$i18n.locale || "en").toLowerCase();

      if (nextLanguage === currentLanguage) {
        return;
      }

      try {
        await this.$store.dispatch("auth/updateMyLanguage", { lang: nextLanguage });
        this.$i18n.locale = nextLanguage;
      } catch (e) {
        this.selectedLanguage = currentLanguage;
      }
    },
  },
  watch: {
    user: {
      handler: function (newVal) {
        this.userData = newVal;
        this.selectedLanguage = (newVal?.lang || this.$i18n.locale || "en").toLowerCase();
      },
      deep: true,
    },
    showAccountDDL(val) {
      // lock/unlock body scroll
      document.body.classList.toggle("no-scroll", val);
      // ESC handler
      if (val) window.addEventListener("keydown", this._onEsc);
      else window.removeEventListener("keydown", this._onEsc);
    },
    // ...existing watchers...
  },
  beforeDestroy() {
    document.body.classList.remove("no-scroll");
    window.removeEventListener("keydown", this._onEsc);
  },
  mounted() {
    this.selectedLanguage = (this.user?.lang || this.$i18n.locale || "en").toLowerCase();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global/global";

.app-header {
  height: 56px;
  background-color: #ffffff;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.10);

  .left-side {
    padding: 16px;
    padding-left: 32px;
  }

  .right-side {
    padding: 16px;
    padding-right: 32px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .header-language {
    height: 24px;
    display: inline-flex;
    align-items: center;
    border: 1px solid $gray-40;
    border-radius: 14px;
    padding: 0 8px;
    margin-right: 12px;
    background: #fff;
  }

  .header-language-label {
    margin: 0 6px 0 0;
    font-size: 10px;
    line-height: 1;
    color: $gray-70;
    text-transform: uppercase;
    font-weight: 700;
  }

  .language-select {
    border: 0;
    background: transparent;
    color: $gray-90;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    padding: 0;
    outline: none;
    cursor: pointer;
  }

  .company-name {
    text-decoration: none;
    color: $gray-90;
  }

  .company-filters {
    padding-left: 16px;
  }

  .company-filter-separator {
    padding-left: 24px;
    padding-right: 24px;
  }

  .user-profile {
    position: relative;
    .username {
      padding-left: 4px;
      color: $gray-80;
    }

    .arrow-icon {
      padding-left: 4px;
    }
  }
}

.profile-photo {
  width: 24px;
  height: 24px;
}

.dropdown-menu {
  margin-top: 7px;
  margin-left: 7px;
  position: absolute;
  top: 40px;
  left: -15px;
  z-index: 1001;
}

.left-side {
  display: flex;
  flex-direction: row;
}

.logo {
  width: 134px;
  height: 20px;
}

.version {
  margin-right: 10px;
}

.login_text {
  color: #ff0202;
  font-weight: 700;
}
</style>
